import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Types matching our database schema
interface HeroContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  cta_primary_en: string;
  cta_primary_ro: string;
  cta_secondary_en: string;
  cta_secondary_ro: string;
  scroll_text_en: string;
  scroll_text_ro: string;
}

interface AboutContent {
  title_en: string;
  title_ro: string;
  bio1_en: string;
  bio1_ro: string;
  bio2_en: string;
  bio2_ro: string;
  bio3_en: string;
  bio3_ro: string;
  stats_title_en: string;
  stats_title_ro: string;
  worked_with_title_en: string;
  worked_with_title_ro: string;
  albums_title_en: string;
  albums_title_ro: string;
}

interface MusicContent {
  latest_release_title_en: string;
  latest_release_title_ro: string;
  latest_release_subtitle_en: string;
  latest_release_subtitle_ro: string;
  youtube_video_id: string;
  youtube_video_title: string;
  spotify_track_id: string;
  spotify_track_title: string;
  vlog_video_id: string;
  vlog_title_en: string;
  vlog_title_ro: string;
  vlog_subtitle: string;
  spotify_artist_url: string;
}

interface ServicesContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  cta_text_en: string;
  cta_text_ro: string;
}

interface ContactContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
  email: string;
  location_en: string;
  location_ro: string;
  artist_name: string;
  artist_alias: string;
}

interface BeatsContent {
  title_en: string;
  title_ro: string;
  subtitle_en: string;
  subtitle_ro: string;
}

interface AnnouncementContent {
  enabled: boolean;
  badge_text: string;
  eyebrow_text_en: string;
  eyebrow_text_ro: string;
  title_en: string;
  title_ro: string;
  highlight_text: string;
  description_en: string;
  description_ro: string;
  primary_button_text: string;
  primary_button_url: string;
  primary_button_type: string;
  secondary_button_text: string;
  secondary_button_url: string;
  secondary_button_type: string;
}

interface SiteSettings {
  site_name: string;
  footer_text: string;
}

interface Album {
  id: number;
  title: string;
  spotify_url?: string;
  meta_en?: string;
  meta_ro?: string;
  icon: string;
  note_en?: string;
  note_ro?: string;
  sort_order: number;
}

interface Collaborator {
  id: number;
  name: string;
  role_en: string;
  role_ro: string;
  icon: string;
  avatar_url?: string;
  sort_order: number;
}

interface Service {
  id: number;
  icon: string;
  title_en: string;
  title_ro: string;
  description_en: string;
  description_ro: string;
  link_url?: string;
  sort_order: number;
}

interface SocialLink {
  id: number;
  name: string;
  url: string;
  icon: string;
  sort_order: number;
}

interface Stat {
  id: number;
  icon: string;
  value: string;
  label_en: string;
  label_ro: string;
  is_dynamic: boolean;
  sort_order: number;
}

interface Beat {
  id: number;
  title: string;
  filename?: string;
  audio_url?: string;
  bpm?: number;
  key?: string;
  tags_en?: string;
  tags_ro?: string;
  for_sale: boolean;
  price?: number;
  sort_order: number;
}

export interface CMSData {
  hero: HeroContent | null;
  about: AboutContent | null;
  music: MusicContent | null;
  servicesContent: ServicesContent | null;
  contact: ContactContent | null;
  beatsContent: BeatsContent | null;
  announcement: AnnouncementContent | null;
  settings: SiteSettings | null;
  albums: Album[];
  collaborators: Collaborator[];
  services: Service[];
  socialLinks: SocialLink[];
  stats: Stat[];
  beats: Beat[];
}

interface CMSContextValue {
  data: CMSData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const CMSContext = createContext<CMSContextValue | undefined>(undefined);

export const CMSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<CMSData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/public/site-data');

      if (!response.ok) {
        throw new Error(`Failed to fetch CMS data: ${response.status}`);
      }

      const cmsData = await response.json();
      setData(cmsData);
    } catch (err) {
      console.error('CMS fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Keep existing data on error (stale-while-revalidate pattern)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <CMSContext.Provider value={{ data, loading, error, refresh: fetchData }}>
      {children}
    </CMSContext.Provider>
  );
};

export const useCMS = (): CMSContextValue => {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within CMSProvider');
  }
  return context;
};

// Helper hook for getting localized content
export const useLocalizedCMS = (lang: 'en' | 'ro') => {
  const { data, loading, error } = useCMS();

  const get = <T extends string>(en: T | undefined, ro: T | undefined, fallback: T): T => {
    if (lang === 'ro' && ro) return ro;
    if (en) return en;
    return fallback;
  };

  return { data, loading, error, get };
};
